// components/DataTable.jsx
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================
*/
import { useMemo, useEffect, useState, useReducer, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Table, TableBody, TableContainer, TableRow, Icon, Autocomplete } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDPagination from 'components/MDPagination';
import useReactTableInstance from 'hooks/useReactTableInstance';
import useFetchData from 'hooks/useFetchData';
import DataTableHeadCell from 'components/DataTablePage/components/DataTable/DataTableHeadCell';
import DataTableBodyRow from './DataTableBodyRow';
import SubRows from './SubRows';

const ACTION = {
  PAGE_CHANGED: 'page-changed',
  PAGE_SIZE_CHANGED: 'page-sized-changed',
  TOTAL_COUNT_CHANGED: 'total-count-changed'
};

const defaultState = {
  queryPageIndex: 0,
  queryPageSize: 20,
  queryTotalCount: 0
};

// 1) single reducer for all three bits of table state
const tableReducer = (state, action) => {
  switch (action.type) {
    case ACTION.PAGE_CHANGED:
      return { ...state, queryPageIndex: action.pageIndexValue };
    case ACTION.PAGE_SIZE_CHANGED:
      return { ...state, queryPageSize: action.pageSizeChangedValue };
    case ACTION.TOTAL_COUNT_CHANGED:
      return { ...state, queryTotalCount: action.totalCountChangedValue };
    default:
      return state;
  }
};

function DataTable({
  showTotalEntries = true,
  pagination = { variant: 'gradient', color: 'info' },
  isSorted = true,
  noEndBorder = false,
  canFilter = false,
  filtersComponent,
  columnData,
  fetchData,
  queryKey,
  object,
  onDelete,
  onView,
  noActions,
  subrowFetchData,
  defaultPageSize = 20,
  filters = {},
  showOnly
}) {
  // delete dialog
  const [openDelete, setOpenDelete] = useState(false);
  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);
  const handleDelete = (id) => {
    onDelete(id);
    handleCloseDelete();
  };

  // 2) set up reducer
  const [tableState, dispatch] = useReducer(tableReducer, {
    ...defaultState,
    queryPageSize: defaultPageSize
  });
  const { queryPageIndex, queryPageSize, queryTotalCount } = tableState;

  // const totalPageCount = Math.ceil(queryTotalCount / queryPageSize);
  const totalPageCount = Math.max(1, Math.ceil(queryTotalCount / queryPageSize));

  // --- reset react-table page when filters change
  useEffect(() => {
    // whenever filters change, start from the first page in react-table
    gotoPage(0);
  }, [
    filters.search,
    filters.roundHash,
    filters.startDate,
    filters.endDate,
    filters.filterType,
    filters.subFilterSelected,
    filters.refetchKey
  ]);

  // 3) handlers to dispatch state changes
  const setQueryPageIndex = (pageIndexValue) => dispatch({ type: ACTION.PAGE_CHANGED, pageIndexValue });
  const setQueryPageSize = (pageSizeChangedValue) => dispatch({ type: ACTION.PAGE_SIZE_CHANGED, pageSizeChangedValue });
  const setTotalCountHandler = ({ totalCountChangedValue }) =>
    dispatch({ type: ACTION.TOTAL_COUNT_CHANGED, totalCountChangedValue });

  // 4) memoize filters so identity only changes when values change
  // const memoFilters = useMemo(() => filters, [filters.startDate, filters.endDate, filters.search]);
  const memoFilters = useMemo(
    () => filters,
    [
      filters.search,
      filters.roundHash,
      filters.startDate,
      filters.endDate,
      filters.filterType,
      filters.subFilterSelected,
      filters.refetchKey
    ]
  );

  // 5) call the hook
  const { data: RES_DATA, isLoading } = useFetchData(
    queryPageIndex,
    queryPageSize,
    queryKey,
    fetchData,
    memoFilters,
    setTotalCountHandler
  );

  const columns = useMemo(() => columnData, [columnData]);
  const data = useMemo(() => RES_DATA.data, [RES_DATA]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageIndex,
    pageSize
  } = useReactTableInstance(columns, data, queryPageIndex, queryPageSize, totalPageCount);

  useEffect(() => {
    gotoPage(0);
    dispatch({ type: ACTION.PAGE_CHANGED, pageIndexValue: 0 });
  }, [
    filters.search,
    filters.roundHash,
    filters.startDate,
    filters.endDate,
    filters.filterType,
    filters.subFilterSelected,
    filters.refetchKey,
    gotoPage
  ]);

  // 6) sync react-table internal page changes back into our reducer
  useEffect(() => {
    setQueryPageIndex(pageIndex);
  }, [pageIndex]);
  useEffect(() => {
    setQueryPageSize(pageSize);
  }, [pageSize]);

  if (isLoading) {
    return <Skeleton variant='rounded' animation='wave' sx={{ borderRadius: 5 }} height={600} />;
  }

  // pagination UI helpers
  const entriesStart = pageIndex * pageSize + 1;
  const entriesEnd = Math.min((pageIndex + 1) * pageSize, queryTotalCount);
  Math.min((pageIndex + 1) * pageSize, queryTotalCount);

  const renderPages = pageOptions.map((opt) => (
    <MDPagination item key={opt} onClick={() => gotoPage(opt)} active={pageIndex === opt}>
      {opt + 1}
    </MDPagination>
  ));

  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      {(queryPageSize || canFilter) && (
        <MDBox
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1}
          px={{ xs: 1.5, md: 3 }}
          py={1.5}
          sx={({ palette: { light }, borders: { borderWidth } }) => ({
            borderBottom: `${borderWidth[1]} solid ${light.main}`
          })}
        >
          {queryPageSize && (
            <MDBox display='flex' alignItems='center' gap={1} flexShrink={0}>
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={['5', '10', '15', '20', '25', '50', '100', '200']}
                onChange={(_, v) => setPageSize(parseInt(v, 10))}
                size='small'
                sx={{
                  width: '5.5rem',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 34 }
                }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant='caption' color='secondary' fontWeight='medium'>
                rows per page
              </MDTypography>
            </MDBox>
          )}
          <MDBox display='flex' alignItems='center' width='100%' sx={{ overflowX: 'auto' }}>
            {canFilter && filtersComponent}
          </MDBox>
        </MDBox>
      )}

      <MDBox sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table {...getTableProps()} sx={{ minWidth: 500 }}>
          <MDBox component='thead'>
            {headerGroups.map((hg) => (
              <TableRow {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <DataTableHeadCell
                    {...col.getHeaderProps(isSorted && col.getSortByToggleProps())}
                    width={col.width || 'auto'}
                    align={col.align || 'left'}
                    sorted={col.sorted ?? (col.isSorted ? (col.isSortedDesc ? 'desc' : 'asce') : 'none')}
                  >
                    {col.render('Header')}
                  </DataTableHeadCell>
                ))}
                {showOnly && noActions && (
                  <DataTableHeadCell width='0.5rem' align='left' sorted={false}>
                    Actions
                  </DataTableHeadCell>
                )}
              </TableRow>
            ))}
          </MDBox>

          <TableBody {...getTableBodyProps()}>
            {page.map((row, idx) => {
              prepareRow(row);
              return (
                <Fragment key={idx}>
                  <DataTableBodyRow
                    row={row}
                    object={object}
                    noActions={noActions}
                    noEndBorder={noEndBorder}
                    openDelete={openDelete}
                    handleCloseDelete={handleCloseDelete}
                    handleDelete={handleDelete}
                    onView={onView}
                    rowsLength={page.length}
                    showOnly={showOnly}
                    rowIndex={idx}
                  />
                  {row.isExpanded && <SubRows row={row} rowProps={row.getRowProps()} subrowFetchData={subrowFetchData} />}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </MDBox>

      <MDBox
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
        px={{ xs: 1.5, md: 3 }}
        py={2}
      >
        {showTotalEntries && (
          <MDTypography variant='caption' color='secondary' fontWeight='medium'>
            Showing <strong>{entriesStart}</strong>–<strong>{entriesEnd}</strong> of <strong>{queryTotalCount}</strong> entries
          </MDTypography>
        )}
        {pageOptions.length > 1 && (
          <MDPagination variant={pagination.variant} color={pagination.color}>
            {canPreviousPage && (
              <MDPagination item onClick={previousPage}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPages.length > 6 ? (
              <MDInput type='number' value={pageIndex + 1} onChange={(e) => gotoPage(Number(e.target.value - 1))} />
            ) : (
              renderPages
            )}
            {canNextPage && (
              <MDPagination item onClick={nextPage}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

DataTable.propTypes = {
  showTotalEntries: PropTypes.bool,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark', 'light'])
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  canFilter: PropTypes.bool,
  filtersComponent: PropTypes.node,
  columnData: PropTypes.array.isRequired,
  fetchData: PropTypes.func.isRequired,
  queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  filters: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    search: PropTypes.string,
    refetchKey: PropTypes.number
  }),
  subrowFetchData: PropTypes.func,
  onDelete: PropTypes.func,
  object: PropTypes.string,
  noActions: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  showOnly: PropTypes.bool
};

export default DataTable;
